import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["draggable", "handleClick", "disableBackdropClick", "children", "top", "TopButton"],
      _excluded2 = ["draggable", "title", "handleClick", "disableBackdropClick", "minHeight", "onPrint", "onFax", "onEmail", "noIcon", "children", "disablePrint", "TopButton", "hideBox", "contentStyle"];

/* eslint-disable no-nested-ternary */
import React, { Fragment, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { DialogContent, Dialog } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import { useIntl } from 'react-intl';
import { Checkmark, Printer } from '@mummssoftware/icons';
import Paper from '@material-ui/core/Paper';
import { SectionHeader } from '../SectionHeader';
import { ActionIcon } from '../ActionIcon';
import messages from '../CardHeaderNav/messages';
const minheight = 0.5 * window.innerHeight;
const minwidth = 0.5 * window.innerWidth; // this is to get the dialog to open at the top if selected

const useStyles = makeStyles(() => createStyles({
  scrollPaper: {
    alignItems: 'baseline' // default center

  },
  root: {
    minHeight: `${minheight}px`,
    minWidth: `${minwidth}px`,
    padding: `8px 8px`
  },
  draggableroot: {
    padding: `8px 0px`
  }
})); // create a blur background on opening dialog

/*
To clarify: this selector selects all elements (*)...
...that are children of the body (body > *)
...and do not have ID loading (*:not(#dialog)).
*/

const GlobalStyle = createGlobalStyle`
  body > *:not(#dialog) {
    filter: blur(1px);
  }
`;
const StyledDialog = styled(Dialog).withConfig({
  displayName: "Dialog__StyledDialog",
  componentId: "sc-1ffjsfq-0"
})(["&&{pointer-events:none;}& .resizable{position:relative;border:3px solid ", ";border-top:none;overflow:hidden;}& .resizable > .react-resizable-handle{position:absolute;width:20px;height:20px;bottom:0;right:0;background:url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+');background-position:bottom right;padding:0 3px 3px 0;background-repeat:no-repeat;background-origin:content-box;box-sizing:border-box;cursor:se-resize;}"], props => props.theme.palette.background.header);
const StyledPaper = styled(Paper).withConfig({
  displayName: "Dialog__StyledPaper",
  componentId: "sc-1ffjsfq-1"
})(["pointer-events:auto;"]);
const StyledDialogContent = styled(DialogContent).withConfig({
  displayName: "Dialog__StyledDialogContent",
  componentId: "sc-1ffjsfq-2"
})(["&&.MuiDialogContent-root{border:3px solid ", ";border-top:none;}"], props => props.theme.palette.background.header);

function PaperComponent(props) {
  const widgetContainerRef = React.useRef(null);
  let bounds = {
    left: 0,
    top: 0,
    bottom: 0,
    right: 0
  };

  const onStart = (event, uiData) => {
    var _window, _window$document, _widgetContainerRef$c;

    const {
      clientWidth,
      clientHeight
    } = (_window = window) === null || _window === void 0 ? void 0 : (_window$document = _window.document) === null || _window$document === void 0 ? void 0 : _window$document.documentElement;
    const targetRect = widgetContainerRef === null || widgetContainerRef === void 0 ? void 0 : (_widgetContainerRef$c = widgetContainerRef.current) === null || _widgetContainerRef$c === void 0 ? void 0 : _widgetContainerRef$c.getBoundingClientRect();

    if (targetRect) {
      bounds = {
        left: -(targetRect === null || targetRect === void 0 ? void 0 : targetRect.left) + (uiData === null || uiData === void 0 ? void 0 : uiData.x),
        right: clientWidth - ((targetRect === null || targetRect === void 0 ? void 0 : targetRect.right) - (uiData === null || uiData === void 0 ? void 0 : uiData.x)),
        top: -(targetRect === null || targetRect === void 0 ? void 0 : targetRect.top) + (uiData === null || uiData === void 0 ? void 0 : uiData.y),
        bottom: clientHeight - ((targetRect === null || targetRect === void 0 ? void 0 : targetRect.bottom) - (uiData === null || uiData === void 0 ? void 0 : uiData.y))
      };
    }
  };

  return /*#__PURE__*/React.createElement(Draggable, {
    onStart: (event, uiData) => onStart(event, uiData),
    bounds: bounds,
    handle: "#draggable-dialog-title",
    cancel: '[class*="MuiDialogContent-root"]',
    nodeRef: widgetContainerRef
  }, /*#__PURE__*/React.createElement("div", {
    ref: widgetContainerRef
  }, /*#__PURE__*/React.createElement(StyledPaper, props)));
}

const dragStyle = {
  cursor: 'move'
};

const DialogHeader = ({
  handleClick = () => null,
  onPrint,
  onFax,
  onEmail,
  style,
  id,
  title,
  noIcon,
  disablePrint,
  TopButton
}) => {
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const {
    formatMessage
  } = useIntl();
  const hasPrintMenu = typeof onPrint === 'function' && (typeof onFax === 'function' || typeof onEmail === 'function');

  const handlePrintIconClick = e => {
    if (hasPrintMenu) {
      setPrintMenuAnchor(e.currentTarget);
    } else if (typeof onPrint === 'function') {
      onPrint();
    }
  };

  const handlePrintMenuClose = () => setPrintMenuAnchor(null);

  const handlePrint = () => {
    handlePrintMenuClose();
    if (typeof onPrint === 'function') onPrint();
  };

  const handleFax = () => {
    handlePrintMenuClose();
    if (typeof onFax === 'function') onFax();
  };

  const handleEmail = () => {
    handlePrintMenuClose();
    if (typeof onEmail === 'function') onEmail();
  };

  return /*#__PURE__*/React.createElement(SectionHeader, {
    title: title,
    style: style,
    id: id,
    nav: /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
      style: {
        float: 'left',
        marginTop: '10px',
        height: '40px'
      }
    }, TopButton), (onPrint || onFax || onEmail) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ActionIcon, {
      "data-testid": "dialog-print",
      Icon: Printer,
      onClick: hasPrintMenu ? handlePrintIconClick : onPrint,
      disabled: disablePrint
    }), hasPrintMenu && /*#__PURE__*/React.createElement(Menu, {
      anchorEl: printMenuAnchor,
      open: Boolean(printMenuAnchor),
      onClose: handlePrintMenuClose,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'center'
      }
    }, /*#__PURE__*/React.createElement(MenuItem, {
      onClick: handlePrint
    }, formatMessage(messages.print)), typeof onFax === 'function' && /*#__PURE__*/React.createElement(MenuItem, {
      onClick: handleFax
    }, formatMessage(messages.fax)), typeof onEmail === 'function' && /*#__PURE__*/React.createElement(MenuItem, {
      onClick: handleEmail
    }, "Email"))), noIcon ? null : /*#__PURE__*/React.createElement(ActionIcon, {
      "data-testid": "dialog-done",
      Icon: Checkmark,
      onClick: handleClick
    }))
  });
};
/* disableEnforceFocus : the modal will not prevent focus from leaving the modal while open.
Generally this should never be set to true as it makes the modal less accessible to assistive technologies, like screen readers.
We need it for cert-app and clearscripts signed verbal tab
*/
// TODO Invalid prop children of type array supplied to resizableBox
// top to have the dialog box open on top of page instead of middle default


const DialogWrap = _ref => {
  let {
    draggable,
    handleClick,
    disableBackdropClick,
    children,
    top,
    TopButton
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  let Container;
  const classes = useStyles();
  const height = 0.75 * window.innerHeight;
  const width = 0.65 * window.innerWidth;

  if (draggable) {
    const bounds = {};
    Container = /*#__PURE__*/React.createElement(StyledDialog, _extends({
      id: "dialog",
      PaperComponent: PaperComponent,
      hideBackdrop: props.hideBackdrop,
      maxWidth: false,
      "aria-labelledby": "draggable-dialog-title",
      bounds: bounds
    }, props), /*#__PURE__*/React.createElement(ResizableBox, {
      height: height,
      width: width,
      className: "resizable"
    }, children));
  } else {
    Container = /*#__PURE__*/React.createElement(Dialog, _extends({
      id: "dialog",
      classes: top ? {
        scrollPaper: classes.scrollPaper
      } : {},
      "aria-labelledby": "dialog-title",
      scroll: "paper" // body
      ,
      disableScrollLock: true,
      disableEnforceFocus: true // Let the user focus on elements outside the dialog
      ,
      onClose: (event, reason) => {
        if (disableBackdropClick) {
          if (reason !== 'backdropClick') {
            handleClick();
          }
        } else {
          handleClick();
        }
      }
    }, props), TopButton, children);
  }

  return Container;
};

export const DialogBox = _ref2 => {
  let {
    draggable,
    title,
    handleClick,
    disableBackdropClick,
    minHeight,
    onPrint,
    onFax,
    onEmail,
    noIcon,
    children,
    disablePrint,
    TopButton,
    hideBox,
    contentStyle
  } = _ref2,
      props = _objectWithoutProperties(_ref2, _excluded2);

  const classes = useStyles();
  const style = draggable ? dragStyle : undefined;
  return /*#__PURE__*/React.createElement(DialogWrap, _extends({
    id: "dialog",
    draggable: draggable,
    disableBackdropClick: disableBackdropClick,
    handleClick: handleClick
  }, props), hideBox ? children : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DialogHeader, {
    id: `${draggable ? 'draggable-' : ''}dialog-title`,
    title: title,
    style: style,
    handleClick: handleClick,
    onPrint: onPrint,
    onFax: onFax,
    onEmail: onEmail,
    noIcon: noIcon,
    disablePrint: disablePrint,
    TopButton: TopButton
  }), draggable ? /*#__PURE__*/React.createElement(DialogContent, {
    classes: {
      root: classes.draggableroot
    }
  }, children) : /*#__PURE__*/React.createElement(StyledDialogContent, {
    style: contentStyle,
    classes: minHeight ? {
      root: classes.root
    } : {}
  }, children)));
};
export default DialogBox;